import type { ShadowSpec } from '~/design-system/components/DeckGen/types/presentation'

/**
 * Structured ⇄ CSS conversion for box-shadow, mirroring `utils/gradientFill`: the structured `ShadowSpec[]`
 * is the editable source of truth and `boxShadow` is the serialized CSS string the renderer uses. Kept pure
 * and dependency-free.
 */

/** Serialize a shadow stack to a CSS `box-shadow` string, or undefined when empty (→ no shadow). Each spec
 *  emits `[inset] Xpx Ypx Blurpx Spreadpx color`; multiple specs join with ', ' (CSS paints them front-to
 *  -back in list order). */
export function shadowsToCss(shadows: ShadowSpec[] | undefined): string | undefined {
  if (!shadows || shadows.length === 0) return undefined
  const parts = shadows.map(
    (s) =>
      `${s.inset ? 'inset ' : ''}${round(s.x)}px ${round(s.y)}px ${round(Math.max(0, s.blur))}px ${round(s.spread)}px ${s.color}`,
  )
  return parts.join(', ')
}

/** Best-effort parse of a CSS `box-shadow` string into structured specs, so a baked shadow opens editable
 *  rather than a frozen string. Returns null when the value is empty/'none' or nothing parses. Handles
 *  multiple comma-separated shadows, `inset`, a leading or trailing color, and an omitted spread. */
export function cssToShadows(css: string | undefined | null): ShadowSpec[] | null {
  if (!css) return null
  const trimmed = css.trim()
  if (!trimmed || trimmed === 'none') return null
  const specs: ShadowSpec[] = []
  for (const part of splitTopLevel(trimmed)) {
    const spec = parseOneShadow(part)
    if (spec) specs.push(spec)
  }
  return specs.length ? specs : null
}

function round(n: number): number {
  return Math.round(n)
}

/** Split a comma-separated list at top level only — commas inside `rgb()/rgba()/hsl()` parens are kept. */
function splitTopLevel(value: string): string[] {
  const out: string[] = []
  let depth = 0
  let current = ''
  for (const ch of value) {
    if (ch === '(') depth++
    else if (ch === ')') depth = Math.max(0, depth - 1)
    if (ch === ',' && depth === 0) {
      out.push(current.trim())
      current = ''
    } else {
      current += ch
    }
  }
  if (current.trim()) out.push(current.trim())
  return out
}

/** Parse a single shadow token: `[inset] <color?> <len> <len> [len] [len] <color?>`. Pulls the color
 *  (hex / rgb() / rgba() / hsl() / named) out first, then reads the remaining lengths as x/y/blur/spread. */
function parseOneShadow(token: string): ShadowSpec | null {
  let rest = token.trim()
  if (!rest) return null

  let inset = false
  if (/(^|\s)inset(\s|$)/i.test(rest)) {
    inset = true
    rest = rest.replace(/(^|\s)inset(\s|$)/i, ' ').trim()
  }

  // Pull out a color token (functional rgb/rgba/hsl, hex, or a bare CSS keyword) and remove it.
  let color = '#000000'
  const fn = rest.match(/(?:rgba?|hsla?)\([^)]*\)/i)
  if (fn) {
    color = fn[0]
    rest = rest.replace(fn[0], ' ').trim()
  } else {
    const hex = rest.match(/#[0-9a-f]{3,8}\b/i)
    if (hex) {
      color = hex[0]
      rest = rest.replace(hex[0], ' ').trim()
    } else {
      // A trailing/leading bare color keyword (e.g. "black"): a non-numeric, non-`inset` word.
      const word = rest.match(/(^|\s)([a-z]+)(\s|$)/i)
      if (word && !/^\d/.test(word[2])) {
        color = word[2]
        rest = rest.replace(word[0], ' ').trim()
      }
    }
  }

  const nums = (rest.match(/-?\d*\.?\d+/g) ?? []).map(Number).filter((n) => Number.isFinite(n))
  if (nums.length < 2) return null // need at least x + y to be a real shadow
  const [x, y, blur = 0, spread = 0] = nums
  return { x: round(x), y: round(y), blur: round(Math.max(0, blur)), spread: round(spread), color, ...(inset ? { inset: true } : {}) }
}
